import { html, nothing } from "lit";
import type { CompactionStatus } from "../app-tool-stream.ts";
import type { AssistantIdentity } from "../assistant-identity.ts";
import { icons } from "../icons.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { formatChatWaitElapsed } from "./chat-wait-elapsed.ts";
import { renderCollapsibleMarkdownPrompt } from "./collapsible-markdown-prompt.ts";
import { renderAvatar, renderGroupedMessage } from "./grouped-render.ts";
import { renderReasoningBlock } from "./reasoning-block.ts";
import { filterActiveSubagents } from "./subagent-run-status.ts";
import { selectThinkingContent } from "./thinking-selector.ts";
import { pickThinkingIcon, renderThinkingIcon } from "./thinking-gif.ts";
import { extractToolCards } from "./tool-cards.ts";

export function hasActiveToolInStream(toolMessages: unknown[]): boolean {
  for (const msg of toolMessages) {
    const cards = extractToolCards(msg);
    const call = cards.find((c) => c.kind === "call");
    const result = cards.find((c) => c.kind === "result");
    if (call && !result) {
      return true;
    }
  }
  return false;
}

export type LiveRunStepState = "active" | "done" | "pending";

export type LiveRunStep = {
  id: string;
  label: string;
  /**
   * JAISIU-types-sweep: nullable as well as optional. A step can be
   * "known but not detailed yet" (the builders leave it `undefined`
   * until a task / file path exists), and a partial step handed to the
   * renderer carries an explicit `null`. The only consumer gates on
   * truthiness (`step.detail ? html`…` : nothing`), so `null` and
   * `undefined` render identically.
   */
  detail?: string | null;
  state: LiveRunStepState;
};

export function buildLiveRunSteps(params: {
  toolMessageCount: number;
  hasActiveTool: boolean;
  /** True while the gateway run is in progress (not only during chat.send HTTP). */
  runActive: boolean;
  streamText: string;
  reasoningStreamText?: string;
  compactionStatus: CompactionStatus | null;
  subagents: ChatSubagentCard[];
  liveRunHint?: string | null;
  /**
   * Sticky fallback for `liveRunHint`. When `liveRunHint` is empty/null
   * (e.g. between phases where the gateway briefly clears the hint), the
   * step label keeps the previous non-empty hint so the chip does not
   * flash `null → "Reading…" → null`. Only a *different* non-empty hint
   * replaces the sticky. The `null → null` cycle is dropped.
   *
   * The caller is responsible for remembering the sticky across renders.
   * `nextLiveRunStepsResult` is a small helper that returns the new
   * sticky hint together with the steps so callers don't have to derive
   * it by hand.
   */
  stickyLiveRunHint?: string | null;
  activeFileToolPath?: string | null;
  /**
   * JAISIU-3752 — one-line summary of the model's most recent thinking,
   * derived by `resolveLiveThinkingSummary`. When non-empty, the live
   * assistant bubble and the composer pill render it as the primary
   * step label (after subagent / live-hint / file-tool / compaction
   * which are more actionable). The thinking summary replaces the
   * generic `Thinking…` / `Reasoning…` fallback that operators had to
   * stare at when the Inspector was closed.
   */
  thinkingSummary?: string | null;
  /**
   * Sticky fallback for `thinkingSummary`. Same latching contract as
   * `stickyLiveRunHint`: first non-empty summary sticks; only a
   * *different* non-empty summary replaces it; the `null → null`
   * cycle is dropped. The caller is responsible for remembering the
   * sticky across renders. The companion helper
   * `buildLiveRunStepsWithSticky` returns the next sticky together
   * with the steps.
   */
  stickyThinkingSummary?: string | null;
  /**
   * Clock for the subagent staleness budget. Injected rather than read from
   * `Date.now()` so a render is reproducible and the threshold is testable
   * without sleeping.
   */
  nowMs?: number;
}): LiveRunStep[] {
  const steps: LiveRunStep[] = [];
  const streamTrim = params.streamText.trim();
  const reasoningTrim = params.reasoningStreamText?.trim() ?? "";

  if (params.compactionStatus?.active) {
    steps.push({
      id: "compaction",
      label: "Compacting context…",
      state: "active",
    });
  }

  // `filterActiveSubagents` already drops terminal and wedged cards, so every
  // card reaching the body here is genuinely in-flight.
  for (const card of filterActiveSubagents(params.subagents, params.nowMs)) {
    const label =
      card.toolHint?.trim() ||
      (card.phase === "thinking" ? "Thinking" : card.phase ? String(card.phase) : "") ||
      `Subagent · ${card.label}`;
    steps.push({
      id: `sub:${card.id}`,
      label,
      detail: card.task?.trim() || undefined,
      state: "active",
    });
  }

  const hint = params.liveRunHint?.trim();
  const sticky = params.stickyLiveRunHint?.trim();
  // Pick the hint for the live-run step. The first non-empty hint latches
  // (via the caller's `stickyLiveRunHint`); only a different non-empty
  // hint replaces it. An empty current hint keeps the sticky so the chip
  // does not flicker when the gateway briefly clears the value between
  // events. When both are empty we drop the step entirely (the `null →
  // null` cycle).
  const effectiveHint = hint || sticky || "";
  const filePath = params.activeFileToolPath?.trim();
  if (effectiveHint && params.runActive) {
    steps.push({
      id: "live-hint",
      label: effectiveHint,
      detail: filePath ? `\`${filePath}\`` : undefined,
      state: "active",
    });
  } else if (filePath && params.runActive) {
    steps.push({
      id: "file-tool",
      label: "Editing file…",
      detail: `\`${filePath}\``,
      state: "active",
    });
  }

  if (params.runActive) {
    // JAISIU-composer-pill-missing-states: drop the (reasoningTrim ||
    // !streamTrim) gate that previously short-circuited the entire
    // block. That gate meant a chip would never render while the
    // model was streaming visible text — the operator saw the
    // composer footer fall back to "Awaiting reply · Ns" even on a
    // hot run with active tool calls. Each branch here self-gates on
    // its own signal (hasActiveTool, reasoningTrim, toolMessageCount),
    // and the trailing `!steps.some(...)` guard still ensures we only
    // emit the default "Thinking…" chip when no more specific step
    // (subagent / live-hint / file-tool / compaction) has already
    // fired above. The stream-text length is intentionally NOT a gate
    // here — stream text is already on the canvas, the chip just
    // tells the operator which phase the model is in.
    //
    // JAISIU-3752 — surface the live thinking summary. Priority order:
    //   1. compaction / subagent / live-hint / file-tool (already pushed
    //      above — these are the most actionable signals and always win).
    //   2. tool-active (Running tools…) — when the model is in the tool
    //      phase, the summary line is stale and the tool status wins.
    //   3. thinking-summary (current || sticky) — the actual model
    //      message, which is what the operator reads in the Inspector
    //      Activity panel. Replaces the generic Reasoning / Generating /
    //      Thinking labels for runs that stream a reasoning delta.
    //   4. reasoning / generating / thinking — generic fallbacks for
    //      runs that never surface a thinking line (older models, non-
    //      thinking providers, or runs that have only just started and
    //      have not streamed yet).
    if (params.hasActiveTool) {
      steps.push({ id: "tool-active", label: "Running tools…", state: "active" });
    } else if (!steps.some((s) => s.state === "active")) {
      // Only enter the thinking-summary / generic-fallback chain when
      // no top-tier step (compaction / subagent / live-hint / file-tool)
      // already fired. The top-tier signals are louder than the model's
      // own thinking line and the operator should see them first.
      //
      // JAISIU-3752 — surface the live thinking summary. Priority order:
      //   1. thinking-summary (current || sticky) — the actual model
      //      message, which is what the operator reads in the Inspector
      //      Activity panel. Replaces the generic Reasoning / Generating /
      //      Thinking labels for runs that stream a reasoning delta.
      //   2. reasoning — when no summary is provided but a reasoning
      //      stream is active.
      //   3. generating — when prior tool messages have landed.
      //   4. thinking — last-resort fallback for runs that never surface
      //      a thinking line (older models, non-thinking providers, or
      //      runs that have only just started and have not streamed yet).
      const summary = params.thinkingSummary?.trim() || params.stickyThinkingSummary?.trim() || "";
      if (summary) {
        steps.push({ id: "thinking-summary", label: summary, state: "active" });
      } else if (reasoningTrim) {
        steps.push({ id: "reasoning", label: "Reasoning…", state: "active" });
      } else if (params.toolMessageCount > 0) {
        steps.push({ id: "generating", label: "Generating response…", state: "active" });
      } else {
        steps.push({ id: "thinking", label: "Thinking…", state: "active" });
      }
    }
  }

  return steps;
}

/**
 * Build the live-run steps for a render and return the sticky hint the
 * caller should pass on the next render. Lets callers wire the sticky
 * without re-deriving it from the returned step list.
 *
 * Invariants:
 * - First non-empty `liveRunHint` latches into `nextStickyLiveRunHint`.
 * - Only a *different* non-empty hint replaces the sticky.
 * - When both the current hint and the existing sticky are empty, the
 *   returned sticky is `null` (the `null → null` cycle is dropped).
 * - Same invariants hold for the `thinkingSummary` → `nextStickyThinkingSummary`
 *   pair. The two stickies are tracked independently so a liveRunHint
 *   change (e.g. "Web search…") can flicker through while the thinking
 *   summary stays steady on its latched value.
 */
export function buildLiveRunStepsWithSticky(params: {
  toolMessageCount: number;
  hasActiveTool: boolean;
  runActive: boolean;
  streamText: string;
  reasoningStreamText?: string;
  compactionStatus: CompactionStatus | null;
  subagents: ChatSubagentCard[];
  liveRunHint?: string | null;
  stickyLiveRunHint?: string | null;
  activeFileToolPath?: string | null;
  thinkingSummary?: string | null;
  stickyThinkingSummary?: string | null;
}): {
  steps: LiveRunStep[];
  nextStickyLiveRunHint: string | null;
  nextStickyThinkingSummary: string | null;
} {
  const steps = buildLiveRunSteps(params);
  const current = params.liveRunHint?.trim() ?? "";
  const sticky = params.stickyLiveRunHint?.trim() ?? "";
  let nextStickyLiveRunHint: string | null;
  if (current) {
    // Replace the sticky only when the new hint is *different* from what
    // we already latched. Re-asserting the same hint is a no-op so the
    // caller's `===` check on the returned sticky stays stable across
    // re-renders that don't change anything.
    nextStickyLiveRunHint = current === sticky ? sticky || null : current;
  } else {
    // Keep the existing sticky so the chip does not blank between
    // transient nulls. Drop the `null → null` cycle when there's nothing
    // to remember.
    nextStickyLiveRunHint = sticky || null;
  }

  const currentSummary = params.thinkingSummary?.trim() ?? "";
  const stickySummary = params.stickyThinkingSummary?.trim() ?? "";
  let nextStickyThinkingSummary: string | null;
  if (currentSummary) {
    nextStickyThinkingSummary =
      currentSummary === stickySummary ? stickySummary || null : currentSummary;
  } else {
    nextStickyThinkingSummary = stickySummary || null;
  }

  return { steps, nextStickyLiveRunHint, nextStickyThinkingSummary };
}

function renderStepIcon(state: LiveRunStepState) {
  if (state === "done") {
    return html`<span
      class="chat-assistant-run__icon chat-assistant-run__icon--done"
      aria-hidden="true"
      >${icons.check}</span
    >`;
  }
  if (state === "active") {
    return html`<span
      class="chat-assistant-run__icon chat-assistant-run__icon--active"
      aria-hidden="true"
      >${icons.loader}</span
    >`;
  }
  return html`
    <span
      class="chat-assistant-run__icon chat-assistant-run__icon--pending"
      aria-hidden="true"
    ></span>
  `;
}

const LAST_REASONING_LINES = 50;

/** Return the last N lines of reasoning text for streaming display. */
function lastReasoningLines(text: string, n = LAST_REASONING_LINES): string {
  const lines = text.split(/\r?\n/);
  if (lines.length <= n) {
    return text;
  }
  return lines.slice(-n).join("\n");
}

export function renderChatLiveRunGroup(params: {
  steps: LiveRunStep[];
  streamText: string;
  reasoningStreamText?: string | null;
  /** Session message history — used to surface real reasoning text
   *  inside the "Thinking…" placeholder when the live stream is
   *  empty but the most recent assistant message has stored
   *  thinking content. Sourced from the same `props.messages` the
   *  activity panel reads through the shared selector. */
  messages?: unknown[];
  startedAt: number;
  waitNowMs?: number;
  assistant?: AssistantIdentity;
  showReasoning: boolean;
  transcriptMode?: boolean;
  reasoningExpanded?: boolean;
  onOpenSidebar?: (content: string) => void;
  onOpenViewer?: (markdown: string, messageId: string) => void;
}) {
  const timestamp = new Date(params.startedAt).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  const name = params.assistant?.name ?? "Assistant";
  const streamTrim = params.streamText.trim();
  const reasoningTrim = params.reasoningStreamText?.trim() ?? "";
  // Resolve the canonical thinking content for the trailing message
  // through the shared selector — same source the activity panel
  // uses. Priority: live `reasoningStream` → live `stream` →
  // stored `extractThinking` on the last message. This keeps the
  // placeholder, the activity dock, and the reasoning <details>
  // block in lock-step so the operator never sees a "Thinking…"
  // literal while real content is available.
  const lastMessageIndex = params.messages ? params.messages.length - 1 : -1;
  const selectorResult =
    lastMessageIndex >= 0
      ? selectThinkingContent(
          {
            messages: params.messages ?? [],
            reasoningStream: params.reasoningStreamText ?? null,
            stream: params.streamText,
          },
          { scope: "message", messageIndex: lastMessageIndex },
        )
      : { text: null, source: "history" as const, id: null };
  const liveThinkingText = selectorResult.text?.trim() ?? "";
  const hasSteps = params.steps.length > 0;
  // Show the reasoning block whenever reasoning is enabled — auto-expands on stream.
  const reasoningActive = params.showReasoning && Boolean(reasoningTrim);
  const waitElapsed = formatChatWaitElapsed(params.startedAt, params.waitNowMs ?? Date.now());
  const hasContent = hasSteps || reasoningActive || streamTrim;
  // The fallback "Thinking…" bubble is the only state with no streamed content.
  // Pick a stable random thinking animation for this run so the gif stays
  // consistent across re-renders while the bubble lingers.
  const isWaiting = !hasContent;
  const thinkingIconId = isWaiting ? pickThinkingIcon(params.startedAt) : null;

  return html`
    <div class="chat-group assistant">
      ${params.transcriptMode ? nothing : renderAvatar("assistant", params.assistant)}
      <div class="chat-group-messages">
        <div
          class="chat-bubble chat-assistant-run fade-in ${
            isWaiting ? "chat-assistant-run--waiting" : ""
          }"
          aria-live="polite"
          data-thinking-gif=${isWaiting && thinkingIconId ? thinkingIconId : nothing}
        >
          ${
            hasSteps
              ? html`
                  <ol class="chat-assistant-run__steps">
                    ${params.steps.map(
                      (step) => html`
                        <li
                          class="chat-assistant-run__step chat-assistant-run__step--${step.state}"
                          data-step-id=${step.id}
                        >
                          ${renderStepIcon(step.state)}
                          <div class="chat-assistant-run__step-body">
                            <span class="chat-assistant-run__step-label">${step.label}</span>
                            ${
                              step.detail
                                ? html`<div class="chat-assistant-run__step-detail">
                                    ${renderCollapsibleMarkdownPrompt({
                                      text: step.detail,
                                      title: "Prompt",
                                    })}
                                  </div>`
                                : nothing
                            }
                          </div>
                        </li>
                      `,
                    )}
                  </ol>
                `
              : nothing
          }
          ${
            reasoningActive
              ? html`
                  <div
                    class="chat-assistant-run__reasoning ${hasSteps ? "chat-assistant-run__section" : ""}"
                  >
                    ${renderReasoningBlock({
                      text: lastReasoningLines(reasoningTrim),
                      streaming: true,
                      defaultOpen: true,
                    })}
                  </div>
                `
              : nothing
          }
          ${
            streamTrim
              ? html`
                  <div
                    class="chat-assistant-run__stream ${hasSteps || reasoningActive ? "chat-assistant-run__section" : ""}"
                  >
                    ${renderGroupedMessage(
                      {
                        role: "assistant",
                        content: [{ type: "text", text: params.streamText }],
                        timestamp: params.startedAt,
                      },
                      {
                        isStreaming: true,
                        showReasoning: params.showReasoning,
                        reasoningExpanded: params.reasoningExpanded,
                        transcriptMode: params.transcriptMode,
                        inlineInRun: true,
                        onOpenViewer: params.onOpenViewer,
                      },
                      params.onOpenSidebar,
                    )}
                  </div>
                `
              : !hasContent
                ? html`
                    <div class="chat-assistant-run__waiting">
                      <span class="chat-assistant-run__waiting-label">
                        ${thinkingIconId ? renderThinkingIcon(thinkingIconId) : nothing}
                        <span class="chat-assistant-run__waiting-text">
                          ${liveThinkingText
                            ? lastReasoningLines(liveThinkingText, LAST_REASONING_LINES)
                            : "Thinking…"}
                        </span>
                        <span class="chat-wait-elapsed" aria-label="Waiting time"
                          >${waitElapsed}</span
                        >
                      </span>
                    </div>
                  `
                : nothing
          }
        </div>
        ${
          params.transcriptMode
            ? nothing
            : html`
                <div class="chat-group-footer">
                  <span class="chat-sender-name">${name}</span>
                  <span class="chat-group-timestamp">${timestamp}</span>
                  <span
                    class="chat-wait-elapsed chat-wait-elapsed--inline"
                    aria-label="Waiting time"
                    >${waitElapsed}</span
                  >
                </div>
              `
        }
      </div>
    </div>
  `;
}
